import { useState, type JSX } from 'react'
import type { ConditionTree } from '@kc3/conversations'
import { useEvaluateRule, type EvaluateResult } from '../../../hooks/useEvaluateRule.js'

export interface RuleTestSidebarProps {
  rule: { conditions: ConditionTree; actions: unknown[] }
  /** Used as a hint for the context schema (placeholder + future autofill). */
  domain: 'routing' | 'automation' | 'escalation'
}

/**
 * Test-against-sample sidebar for a routing/automation/escalation rule.
 * Lets the admin paste a JSON context, click "Run test", and see whether
 * the rule matches plus which actions would fire.
 */
export default function RuleTestSidebar({
  rule,
  domain,
}: RuleTestSidebarProps): JSX.Element {
  const { evaluate, isPending } = useEvaluateRule()
  const [contextJson, setContextJson] = useState<string>('{}')
  const [result, setResult] = useState<EvaluateResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function onRun(): Promise<void> {
    setError(null)
    setResult(null)
    let parsed: Record<string, unknown>
    try {
      const raw = JSON.parse(contextJson) as unknown
      if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
        setError('Context must be a JSON object.')
        return
      }
      parsed = raw as Record<string, unknown>
    } catch (e) {
      setError(`Invalid JSON: ${(e as Error).message}`)
      return
    }
    try {
      const res = await evaluate(rule, parsed)
      setResult(res)
    } catch (e) {
      setError((e as Error).message || 'Evaluation failed')
    }
  }

  return (
    <aside
      aria-label="Rule test sidebar"
      className="rts flex flex-col gap-2 rounded border border-gray-300 p-3"
    >
      <h3 className="text-sm font-semibold">Test rule</h3>
      <label className="text-xs text-gray-600" htmlFor="rts-context">
        Sample context ({domain})
      </label>
      <textarea
        id="rts-context"
        aria-label="Context JSON"
        value={contextJson}
        onChange={(e) => setContextJson(e.target.value)}
        rows={8}
        spellCheck={false}
        className="font-mono rounded border border-gray-300 p-2 text-xs"
      />
      <div>
        <button
          type="button"
          onClick={() => {
            void onRun()
          }}
          disabled={isPending}
          className="rounded border border-gray-300 px-2 py-1 text-xs"
        >
          {isPending ? 'Running…' : 'Run test'}
        </button>
      </div>

      {error !== null && (
        <div role="alert" className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800">
          {error}
        </div>
      )}

      {result !== null && (
        <div role="status" aria-label="Test result" className="flex flex-col gap-1">
          <div className="text-xs">
            <strong>Matches:</strong>{' '}
            <span data-testid="rts-matches">{result.matches ? 'true' : 'false'}</span>
          </div>
          <div className="text-xs">
            <strong>Actions:</strong>
            <pre
              data-testid="rts-actions"
              className="mt-1 overflow-x-auto rounded bg-gray-50 p-2 text-[11px]"
            >
              {JSON.stringify(result.actions, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </aside>
  )
}
